import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TrainingProgramsPreview = () => {
  const [activeProgram, setActiveProgram] = useState(0);

  const trainingPrograms = [
    {
      id: 'ai-fundamentals',
      title: 'Intelligence Artificielle Fondamentale',
      category: 'IA & Technologies',
      level: 'Débutant à Intermédiaire',
      duration: '8 semaines',
      format: 'Hybride',
      price: '2,450€',
      rating: 4.9,
      students: 1247,
      description: 'Programme complet d\'introduction à l\'IA appliquée au secteur automobile, couvrant les concepts fondamentaux jusqu\'aux implémentations pratiques.',
      highlights: [
        'Machine Learning appliqué à l\'automobile',
        'Vision par ordinateur pour la conduite autonome',
        'Traitement du langage naturel',
        'Éthique et IA responsable',
        'Projet pratique avec données réelles'
      ],
      modules: [
        { name: 'Fondamentaux IA', duration: '1 semaine', type: 'Théorie + Pratique' },
        { name: 'Machine Learning Automotive', duration: '2 semaines', type: 'Hands-on' },
        { name: 'Computer Vision', duration: '2 semaines', type: 'Projet' },
        { name: 'NLP Applications', duration: '1 semaine', type: 'Workshop' },
        { name: 'IA Éthique', duration: '1 semaine', type: 'Séminaire' },
        { name: 'Projet Final', duration: '1 semaine', type: 'Évaluation' }
      ],
      outcomes: [
        'Comprendre les algorithmes IA essentiels',
        'Développer des solutions IA pour l\'automobile',
        'Implémenter des systèmes de vision par ordinateur',
        'Évaluer l\'impact éthique des solutions IA'
      ],
      instructor: {
        name: 'Dr. Marie Dubois',
        title: 'Expert IA Automotive',
        experience: '12 ans d\'expérience',
        certifications: ['PhD IA', 'Cert. AutoML', 'Expert AWS ML']
      }
    },
    {
      id: 'digital-leadership',
      title: 'Leadership de la Transformation Digitale',
      category: 'Leadership & Management',
      level: 'Intermédiaire à Avancé',
      duration: '12 semaines',
      format: 'Présentiel + Digital',
      price: '3,850€',
      rating: 4.8,
      students: 892,
      description: 'Formation executive pour diriger efficacement la transformation digitale dans l\'industrie automobile moderne.',
      highlights: [
        'Stratégie de transformation digitale',
        'Change management à grande échelle',
        'Culture d\'innovation organisationnelle',
        'Leadership d\'équipes dispersées',
        'ROI de la transformation digitale'
      ],
      modules: [
        { name: 'Vision Stratégique Digitale', duration: '2 semaines', type: 'Séminaire Executive' },
        { name: 'Change Management', duration: '3 semaines', type: 'Cas Pratiques' },
        { name: 'Innovation Leadership', duration: '2 semaines', type: 'Workshop' },
        { name: 'Équipes Distribuées', duration: '2 semaines', type: 'Simulation' },
        { name: 'Mesure Performance', duration: '2 semaines', type: 'Analytics' },
        { name: 'Projet Transformation', duration: '1 semaine', type: 'Capstone' }
      ],
      outcomes: [
        'Élaborer une stratégie de transformation complète',
        'Diriger le changement organisationnel',
        'Créer une culture d\'innovation durable',
        'Mesurer et optimiser les résultats'
      ],
      instructor: {
        name: 'Pierre-Antoine Martin',
        title: 'Senior Executive Coach',
        experience: '18 ans leadership automotive',
        certifications: ['MBA Digital', 'Cert. Change Mgmt', 'Executive Coach ICF']
      }
    },
    {
      id: 'adaptive-skills',
      title: 'Compétences Adaptatives Avancées',
      category: 'Développement Personnel',
      level: 'Tous Niveaux',
      duration: '6 semaines',
      format: 'Full Digital',
      price: '1,750€',
      rating: 4.7,
      students: 2103,
      description: 'Développez votre capacité d\'adaptation et d\'apprentissage continu dans un environnement technologique en évolution rapide.',
      highlights: [
        'Agilité cognitive et mentale',
        'Apprentissage accéléré',
        'Résilience professionnelle',
        'Innovation personnelle',
        'Networking stratégique'
      ],
      modules: [
        { name: 'Mindset Adaptatif', duration: '1 semaine', type: 'Psychologie Cognitive' },
        { name: 'Techniques d\'Apprentissage', duration: '1 semaine', type: 'Neurosciences' },
        { name: 'Résilience & Stress', duration: '1 semaine', type: 'Wellness' },
        { name: 'Créativité & Innovation', duration: '1 semaine', type: 'Design Thinking' },
        { name: 'Réseau Professionnel', duration: '1 semaine', type: 'Social Learning' },
        { name: 'Plan Personnel', duration: '1 semaine', type: 'Coaching' }
      ],
      outcomes: [
        'Développer une mentalité d\'apprentissage continu',
        'Maîtriser les techniques d\'adaptation rapide',
        'Construire une résilience professionnelle',
        'Créer un plan de développement personnel'
      ],
      instructor: {
        name: 'Dr. Sophie Renard',
        title: 'Neuroscientifique & Coach',
        experience: '10 ans recherche appliquée',
        certifications: ['PhD Neurosciences', 'Cert. Executive Coach', 'Spéc. Adaptation']
      }
    }
  ];

  const currentProgram = trainingPrograms?.[activeProgram];

  return (
    <section id="training-programs" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Programmes de Formation Spécialisés</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Nos programmes de formation sont conçus pour développer les compétences critiques 
            identifiées par notre analyse prédictive et notre expertise sectorielle.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Program Selector */}
          <div className="lg:col-span-4 space-y-4">
            {trainingPrograms?.map((program, index) => (
              <div
                key={program?.id}
                onClick={() => setActiveProgram(index)}
                className={`p-6 rounded-xl cursor-pointer transition-all duration-300 ${
                  activeProgram === index
                    ? 'bg-white shadow-lg border-2 border-blue-500 transform scale-105'
                    : 'bg-white shadow-md hover:shadow-lg border border-gray-200'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    activeProgram === index ? 'bg-blue-600' : 'bg-gray-100'
                  }`}>
                    <Icon 
                      name={program?.category?.includes('IA') ? 'Zap' : program?.category?.includes('Leadership') ? 'Users' : 'RefreshCw'} 
                      size={24} 
                      className={activeProgram === index ? 'text-white' : 'text-gray-600'} 
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-bold mb-2 ${
                      activeProgram === index ? 'text-blue-600' : 'text-gray-900'
                    }`}>
                      {program?.title}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                      <span className="bg-gray-100 px-2 py-1 rounded-full">{program?.category}</span>
                      <span>{program?.duration}</span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <Icon name="Star" size={14} className="text-yellow-400 fill-current" />
                        <span className="font-medium">{program?.rating}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Users" size={14} className="text-gray-400" />
                        <span>{program?.students} étudiants</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Program Details */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                  <div>
                    <h3 className="text-3xl font-bold mb-2">{currentProgram?.title}</h3>
                    <p className="text-blue-100 text-lg">{currentProgram?.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{currentProgram?.price}</div>
                    <div className="text-blue-200">par participant</div>
                  </div>
                </div>
              </div>

              {/* Program Info */}
              <div className="p-8 space-y-8">
                {/* Quick Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Icon name="Clock" size={24} className="text-blue-600 mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">{currentProgram?.duration}</div>
                    <div className="text-sm text-gray-600">Durée</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Icon name="Monitor" size={24} className="text-green-600 mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">{currentProgram?.format}</div>
                    <div className="text-sm text-gray-600">Format</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Icon name="BarChart" size={24} className="text-purple-600 mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">{currentProgram?.level}</div>
                    <div className="text-sm text-gray-600">Niveau</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Icon name="Award" size={24} className="text-orange-600 mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">Certifié</div>
                    <div className="text-sm text-gray-600">Diplôme</div>
                  </div>
                </div>

                {/* Highlights */}
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Points Clés du Programme</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {currentProgram?.highlights?.map((highlight, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Modules */}
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Structure du Programme</h4>
                  <div className="space-y-3">
                    {currentProgram?.modules?.map((module, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{module?.name}</div>
                            <div className="text-sm text-gray-600">{module?.type}</div>
                          </div>
                        </div>
                        <div className="text-sm font-medium text-blue-600">{module?.duration}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Learning Outcomes */}
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Résultats d'Apprentissage</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentProgram?.outcomes?.map((outcome, index) => (
                      <div key={index} className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                        <Icon name="Target" size={16} className="text-green-600 flex-shrink-0 mt-1" />
                        <span className="text-gray-700">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructor */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Formateur Expert</h4>
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                      <Icon name="User" size={32} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-lg font-bold text-gray-900">{currentProgram?.instructor?.name}</h5>
                      <p className="text-blue-600 font-medium">{currentProgram?.instructor?.title}</p>
                      <p className="text-gray-600 text-sm mb-2">{currentProgram?.instructor?.experience}</p>
                      <div className="flex flex-wrap gap-2">
                        {currentProgram?.instructor?.certifications?.map((cert, index) => (
                          <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6 border-t border-gray-200">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 shadow-lg">
                    Inscription Immédiate
                  </button>
                  <button className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-lg font-semibold transition-colors duration-200">
                    Demander Informations
                  </button>
                  <button className="border-2 border-gray-300 text-gray-700 hover:border-gray-400 px-8 py-4 rounded-lg font-semibold transition-colors duration-200">
                    Programme Complet PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Pourquoi Choisir Nos Programmes ?</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Des formations conçues par des experts sectoriels, avec un focus sur l'application pratique 
              et les résultats mesurables pour votre organisation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Award" size={32} className="text-blue-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Certification Reconnue</h4>
              <p className="text-sm text-gray-600">Diplômes validés par l'industrie</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={32} className="text-green-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Formateurs Experts</h4>
              <p className="text-sm text-gray-600">15+ années d'expérience secteur</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Target" size={32} className="text-purple-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Approche Pratique</h4>
              <p className="text-sm text-gray-600">Projets réels et cas d'usage</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="TrendingUp" size={32} className="text-orange-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">ROI Mesurable</h4>
              <p className="text-sm text-gray-600">Amélioration performance 2.8x</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrainingProgramsPreview;